import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { getUser } from '../auth';

export default function ModifierActivite() {
  const navigate = useNavigate();
  const { id } = useParams();
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    titre: '', lieu: '', date: '', heure: '',
    ageMin: '', ageMax: '', maxFamilles: '5', maxEnfants: '10', description: '',
  });
  const [suggestions, setSuggestions] = useState([]);
  const [lieuValide, setLieuValide] = useState(true);
  const [lieuCoords, setLieuCoords] = useState(null);

  useEffect(() => {
    if (!user) { navigate('/connexion'); return; }
    fetchActivite();
  }, [id]);

  const fetchActivite = async () => {
    const { data, error } = await supabase.from('activites').select('*').eq('id', id).single();
    if (error || !data) { navigate('/feed'); return; }
    if (data.user_id !== user.id) { navigate('/feed'); return; }
    setForm({
      titre: data.titre || '',
      lieu: data.lieu || '',
      date: data.date || '',
      heure: data.heure || '',
      ageMin: data.age_min?.toString() || '',
      ageMax: data.age_max?.toString() || '',
      maxFamilles: data.max_familles?.toString() || '5',
      maxEnfants: data.max_enfants?.toString() || '10',
      description: data.description || '',
    });
    if (data.latitude && data.longitude) {
      setLieuCoords({ lat: data.latitude, lon: data.longitude });
    }
    setLoading(false);
  };

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleLieuChange = async (val) => {
    update('lieu', val);
    setLieuValide(false);
    if (val.length < 3) { setSuggestions([]); return; }
    try {
      const res = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(val)}&limit=5`);
      const data = await res.json();
      setSuggestions(data.features || []);
    } catch (e) { setSuggestions([]); }
  };

  const selectLieu = (s) => {
    update('lieu', s.properties.label);
    setLieuCoords({ lat: s.geometry.coordinates[1], lon: s.geometry.coordinates[0] });
    setLieuValide(true);
    setSuggestions([]);
  };

  const sauvegarder = async () => {
    if (!form.titre.trim()) { alert('Titre obligatoire'); return; }
    if (!form.date) { alert('Date obligatoire'); return; }
    if (!form.heure) { alert('Heure obligatoire'); return; }
    setSaving(true);
    const { error } = await supabase.from('activites').update({
      titre: form.titre.trim(),
      lieu: form.lieu,
      date: form.date,
      heure: form.heure,
      age_min: parseInt(form.ageMin) || 0,
      age_max: parseInt(form.ageMax) || 18,
      max_familles: parseInt(form.maxFamilles),
      max_enfants: parseInt(form.maxEnfants),
      description: form.description.trim(),
      latitude: lieuCoords?.lat || null,
      longitude: lieuCoords?.lon || null,
    }).eq('id', id);
    setSaving(false);
    if (error) { alert('Erreur lors de la sauvegarde'); return; }
    navigate(`/detail/${id}`);
  };

  const S = {
    input: { width: '100%', padding: '14px 16px', border: '2px solid #E8EFE9', borderRadius: 16, fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', color: '#1C2B1E', background: 'white', outline: 'none', boxSizing: 'border-box', marginBottom: 4 },
    label: { fontSize: '0.78rem', fontWeight: 800, color: '#4A6355', marginBottom: 6, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 },
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FAFAF7' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: 12 }}>🌿</div>
        <div style={{ fontFamily: 'Fredoka One, cursive', color: '#3D9A6A' }}>Chargement...</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#FAFAF7' }}>

      {/* HEADER */}
      <div style={{ background: 'white', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12, boxShadow: '0 2px 12px rgba(28,43,30,0.06)', position: 'sticky', top: 0, zIndex: 100 }}>
        <button onClick={() => navigate(`/detail/${id}`)} style={{ width: 38, height: 38, borderRadius: 12, background: '#F3F4F2', border: 'none', fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.1rem', color: '#1C2B1E' }}>Modifier l'activité</div>
          <div style={{ fontSize: '0.7rem', color: '#8A9E8D' }}>Les participants seront informés</div>
        </div>
        <div onClick={() => navigate('/feed')} style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.3rem', cursor: 'pointer' }}>
          <span style={{ color: '#3D9A6A' }}>Woo</span><span style={{ color: '#FF7043' }}>ki</span>
        </div>
      </div>

      <div style={{ padding: '24px 20px 80px' }}>

        {/* TITRE */}
        <div style={{ background: 'white', borderRadius: 24, padding: '20px', boxShadow: '0 4px 20px rgba(28,43,30,0.07)', marginBottom: 16 }}>
          <label style={S.label}>Titre ✏️</label>
          <input style={S.input} value={form.titre} onChange={e => update('titre', e.target.value)} placeholder="Titre de l'activité" />

          <label style={{ ...S.label, marginTop: 12 }}>Point de ralliement 📍</label>
          <div style={{ position: 'relative' }}>
            <input style={{ ...S.input, borderColor: lieuValide ? '#3D9A6A' : '#E8EFE9' }}
              value={form.lieu} onChange={e => handleLieuChange(e.target.value)}
              placeholder="Adresse du lieu" />
            {lieuValide && <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: '#3D9A6A' }}>✅</div>}
            {suggestions.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', borderRadius: 16, boxShadow: '0 8px 32px rgba(28,43,30,0.12)', zIndex: 100, overflow: 'hidden', marginTop: 4 }}>
                {suggestions.map((s, i) => (
                  <div key={i} onClick={() => selectLieu(s)} style={{ padding: '12px 16px', cursor: 'pointer', fontSize: '0.85rem', color: '#1C2B1E', borderBottom: i < suggestions.length - 1 ? '1px solid #F3F4F2' : 'none', display: 'flex', gap: 10 }}>
                    <span>📍</span>
                    <div>
                      <div style={{ fontWeight: 700 }}>{s.properties.label}</div>
                      {s.properties.context && <div style={{ fontSize: '0.72rem', color: '#8A9E8D' }}>{s.properties.context}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* DATE + HEURE */}
        <div style={{ background: 'white', borderRadius: 24, padding: '20px', boxShadow: '0 4px 20px rgba(28,43,30,0.07)', marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Date 📅</label>
              <input style={S.input} type="date" value={form.date}
                min={new Date().toISOString().split('T')[0]}
                onChange={e => update('date', e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Heure ⏱</label>
              <input style={S.input} type="time" value={form.heure} onChange={e => update('heure', e.target.value)} />
            </div>
          </div>

          <label style={{ ...S.label, marginTop: 8 }}>Âge des enfants 👶</label>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <input style={{ ...S.input, flex: 1 }} placeholder="Min" type="number" min="0" max="17" value={form.ageMin} onChange={e => update('ageMin', e.target.value)} />
            <div style={{ color: '#8A9E8D', fontWeight: 700 }}>→</div>
            <input style={{ ...S.input, flex: 1 }} placeholder="Max" type="number" min="1" max="18" value={form.ageMax} onChange={e => update('ageMax', e.target.value)} />
          </div>
        </div>

        {/* FAMILLES + ENFANTS */}
        <div style={{ background: 'white', borderRadius: 24, padding: '20px', boxShadow: '0 4px 20px rgba(28,43,30,0.07)', marginBottom: 16 }}>
          <label style={S.label}>Familles maximum 👨‍👩‍👧</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {['3', '4', '5', '6', '8', '10'].map(n => (
              <div key={n} onClick={() => update('maxFamilles', n)} style={{
                flex: 1, padding: '12px 0', textAlign: 'center',
                background: form.maxFamilles === n ? '#3D9A6A' : '#F3F4F2',
                color: form.maxFamilles === n ? 'white' : '#1C2B1E',
                borderRadius: 12, fontWeight: 800, cursor: 'pointer', fontSize: '0.9rem', transition: 'all 0.2s',
              }}>{n}</div>
            ))}
          </div>

          <label style={S.label}>Enfants maximum 🧒</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {['5', '8', '10', '12', '15', '20'].map(n => (
              <div key={n} onClick={() => update('maxEnfants', n)} style={{
                flex: 1, padding: '12px 0', textAlign: 'center',
                background: form.maxEnfants === n ? '#FF7043' : '#F3F4F2',
                color: form.maxEnfants === n ? 'white' : '#1C2B1E',
                borderRadius: 12, fontWeight: 800, cursor: 'pointer', fontSize: '0.9rem', transition: 'all 0.2s',
              }}>{n}</div>
            ))}
          </div>
        </div>

        {/* DESCRIPTION */}
        <div style={{ background: 'white', borderRadius: 24, padding: '20px', boxShadow: '0 4px 20px rgba(28,43,30,0.07)', marginBottom: 24 }}>
          <label style={S.label}>Description 💬</label>
          <textarea style={{ ...S.input, height: 110, resize: 'none' }}
            placeholder="Conseils, matériel à apporter..."
            value={form.description} onChange={e => update('description', e.target.value)} />
        </div>

        {/* NOTIFICATION PARTICIPANTS */}
        <div style={{ background: '#EDF7F1', borderRadius: 20, padding: '14px 16px', marginBottom: 20, display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: '1.2rem' }}>💬</span>
          <div style={{ fontSize: '0.82rem', color: '#4A6355', lineHeight: 1.5 }}>
            Les participants inscrits recevront une notification pour les informer des modifications.
          </div>
        </div>

        <button onClick={sauvegarder} disabled={saving} style={{ width: '100%', padding: '17px', background: saving ? '#E8EFE9' : '#3D9A6A', color: saving ? '#8A9E8D' : 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1rem', cursor: saving ? 'not-allowed' : 'pointer', boxShadow: saving ? 'none' : '0 6px 20px rgba(61,154,106,0.35)' }}>
          {saving ? '⏳ Sauvegarde...' : '✅ Sauvegarder les modifications'}
        </button>
      </div>
    </div>
  );
}